import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { TRAFFIC_TYPES } from '@/api/common';
import { type OfflineActivityDataType, useOfflineActivity } from '@/api/offlineEvents';
import { Toggle } from '@/components/buttons';
import { RangeFilter } from '@/components/filters';
import {
  type InstantSearchDataTableColumnDef,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Link, SupportLink, Text } from '@/components/typography';
import { AUDIENCE_PRIMARY, TAM_PRIMARY, VISITS_PRIMARY } from '@/constants';
import { StackModeToggleButton } from '@/features/StackModeToggleButton';
import { ViewThroughPercentCell, VisitsTableBarCell } from '@/features/VisitsTableBarCell';
import didClickOnStickyPopover from '@/helper/didClickOnStickyPopover';
import { numberFormat } from '@/helper/numberFormatter';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { TableState } from '@/providers/TableState';
import { ROUTES } from '@/router';

import OfflineActivityTable from './OfflineActivityTable';

const DEFAULT_HIDDEN_COLUMNS = [
  'countPipelineInfluenced',
  'countClosedwonInfluenced',
  'pipelineFirstTouchAttribution',
  'closedWonFirstTouchAttribution',
  'pipelineLastTouchAttribution',
];

const getOccurrencesWithViewThrough = (field: OfflineActivityDataType) => {
  return {
    target: field.targetVisits + Math.max(field.targetViewThroughVisits ?? 0, 0),
    other: field.otherVisits + Math.max(field.otherViewThroughVisits ?? 0, 0),
  };
};

const OfflineActivityTableContainer = () => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { queryState } = useInstantSearchState();
  const [disabledSeries, setDisabledSeries] = useState<TRAFFIC_TYPES[] | undefined>([]);
  const [occurrencesMode, setOccurrencesMode] = useState<'stack' | 'percent'>('percent');
  const [includeViewThroughVisits, setIncludeViewThroughVisits] = useState(false);

  const { offlineActivity, isLoading, error } = useOfflineActivity(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
    audience?.id,
  );

  const [hoveredRow, setHoveredRow] = useState<OfflineActivityDataType | null>(null);
  const [stickyRow, setStickyRow] = useState<{
    element: HTMLElement;
    record: OfflineActivityDataType;
  } | null>(null);

  useOnClickOutside(stickyRow?.element, () => setStickyRow(null), {
    shouldIgnoreEvent: (event) => didClickOnStickyPopover(event.target),
  });

  const maxVisits = useMemo(
    () =>
      offlineActivity?.reduce((max, d) => {
        const occurrences = includeViewThroughVisits
          ? getOccurrencesWithViewThrough(d)
          : { target: d.targetVisits, other: d.otherVisits };

        let total = 0;

        if (audience?.isTAM) {
          if (!disabledSeries?.includes(TRAFFIC_TYPES.TAM)) {
            total += occurrences.target;
          }
        } else {
          if (!disabledSeries?.includes(TRAFFIC_TYPES.AUDIENCE)) {
            total += occurrences.target;
          }
        }

        if (!disabledSeries?.includes(TRAFFIC_TYPES.OTHER)) {
          total += occurrences.other;
        }

        return Math.max(total, max);
      }, 0),
    [offlineActivity, audience?.isTAM, disabledSeries, includeViewThroughVisits],
  );

  const sortFields = ROUTES.vendors.searchParams['v.sortBy'].enumValues;

  const offlineActivityMetrics = useMemo<
    InstantSearchDataTableColumnDef<OfflineActivityDataType>[]
  >(
    () =>
      [
        {
          header: t`Category`,
          id: 'vendorId',
          minSize: 120,
          flex: 1,
          metricSelector: false,
          cell: ({ row }) => row.original.campaignType.vendor.name,
        },
        {
          header: t`Type`,
          id: 'campaignTypeId',
          minSize: 200,
          flex: 1,
          metricSelector: false,
          cell: ({ row }) => row.original.campaignType.name,
        },
        {
          header: t`Spend`,
          id: 'spend',
          size: 110,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('spend'),
          infoPopover: (
            <Trans>
              Automated spend reported through the vendors API connection or manually calculated
              spend input by your team within the{' '}
              <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
                Vendor Spend Page
              </Link>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => numberFormat(row.original.spend, { isCurrency: true, precision: 0 }),
        },
        {
          header: includeViewThroughVisits ? t`Occurrences + View Through` : t`Occurrences/Events`,
          id: 'visits',
          size: 300,
          meta: { align: 'start' },
          enableSorting: sortFields.includes('visits'),
          actionContent: (
            <div className="flex items-center gap-1 py-0.5">
              <Toggle
                variant="ghost"
                size="small"
                shape="square"
                pressed={includeViewThroughVisits}
                aria-label={t`Include view-through visits in chart`}
                onPressedChange={setIncludeViewThroughVisits}
              >
                <Trans>VT</Trans>
              </Toggle>
              <StackModeToggleButton
                mode={occurrencesMode}
                size="small"
                onChange={setOccurrencesMode}
              />
            </div>
          ),
          infoPopover: (
            <Trans>Count of unique interactions from each campaign, task or lead.</Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const occurrences = includeViewThroughVisits
              ? getOccurrencesWithViewThrough(field)
              : { target: field.targetVisits, other: field.otherVisits };

            return (
              <VisitsTableBarCell
                data={[
                  {
                    type: audience?.isTAM ? TRAFFIC_TYPES.TAM : TRAFFIC_TYPES.AUDIENCE,
                    label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                    value: occurrences.target,
                    color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                    disabled: audience?.isTAM
                      ? disabledSeries?.includes(TRAFFIC_TYPES.TAM) === true
                      : disabledSeries?.includes(TRAFFIC_TYPES.AUDIENCE) === true,
                  },
                  {
                    type: TRAFFIC_TYPES.OTHER,
                    label: t`Other Companies`,
                    value: occurrences.other,
                    color: VISITS_PRIMARY,
                    disabled: disabledSeries?.includes(TRAFFIC_TYPES.OTHER) === true,
                  },
                ]}
                maxVisits={maxVisits}
                displayMode={occurrencesMode}
                isOpaque={
                  (stickyRow == null && hoveredRow == null) ||
                  stickyRow?.record === field ||
                  hoveredRow === field
                }
                isTooltipOpen={hoveredRow === field || stickyRow?.record === field}
                isTooltipSticky={stickyRow?.record === field}
                tooltipTitle={field.campaignType.name}
                onStickyTooltipClose={() => setStickyRow(null)}
              />
            );
          },
          cellProps: ({ row }) => {
            const record = row.original;

            return {
              className: 'cursor-pointer',
              onMouseEnter: () => setHoveredRow(record),
              onMouseLeave: () => setHoveredRow(null),
              onClick: (e) => {
                if (didClickOnStickyPopover(e.target)) {
                  return;
                }

                // clicking on the same row again will close the tooltip
                if (stickyRow?.record === record) {
                  setStickyRow(null);
                } else {
                  setStickyRow({ element: e.currentTarget as HTMLElement, record });
                }
              },
            };
          },
        },
        {
          header: t`VT %`,
          id: 'viewThroughPercent',
          meta: { align: 'end' },
          enableSorting: sortFields.includes('viewThroughPercent'),
          size: 80,
          infoPopover: (
            <Trans>
              VT % shows the share of visits that were view-through only. A view-through visit
              happens when someone views tracked content and later visits through a different direct
              source without clicking that tracked content. This metric is calculated as Total VT
              Visits / (Total VT Visits + Total Visits).
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter type="percent" {...props} />,
          cell: ({ row }) => {
            const field = row.original;

            return (
              <ViewThroughPercentCell
                tooltipTitle={field.campaignType.name}
                viewThroughPercent={field.viewThroughPercent}
                rows={[
                  {
                    label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                    color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                    viewThroughVisits: field.targetViewThroughVisits ?? 0,
                  },
                  {
                    label: t`Other Companies`,
                    color: VISITS_PRIMARY,
                    viewThroughVisits: field.otherViewThroughVisits ?? 0,
                  },
                ]}
              />
            );
          },
        },
        {
          header: t`View-Through Visits`,
          id: 'viewThroughVisits',
          meta: { align: 'end' },
          enableSorting: sortFields.includes('viewThroughVisits'),
          size: 140,
          infoPopover: (
            <Trans>
              A view-through visit occurs when a user views an ad or piece of content and does not
              click through but comes to the website directly through a vendor/campaign.{' '}
              <SupportLink variant="body2" color="static-green" path="VIEW_THROUGH_VISITS">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => numberFormat(row.original.viewThroughVisits),
        },
        {
          header: t`Engaged Companies`,
          id: 'engagedCompanies',
          size: 115,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('engagedCompanies'),
          infoPopover: (
            <Trans>Engaged companies are a count of companies which had at least one visit.</Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) =>
            +row.original.engagedCompanies > 0 ? (
              <Text variant="caption1">{numberFormat(row.original.engagedCompanies)}</Text>
            ) : (
              0
            ),
        },
        {
          header: t`Pipeline Influence`,
          id: 'pipelineInfluenced',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineInfluenced'),
          infoPopover: (
            <Trans>
              Pipeline influence is given to each offline event type with at least one visit during
              the date range chosen before the opportunity create date.{' '}
              <SupportLink variant="body2" color="static-green" path="PIPELINE_INFLUENCE">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.pipelineInfluenced;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListPipelineInfluence': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
        {
          header: t`# Pipeline Influenced`,
          id: 'countPipelineInfluenced',
          size: 145,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('countPipelineInfluenced'),
          infoPopover: (
            <Trans>
              Number of opportunities with pipeline influence attributed to this vendor/campaign.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.countPipelineInfluenced;

            return text === 0 ? (
              numberFormat(text)
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListPipelineInfluence': field.campaignType.id,
                })}
              >
                {numberFormat(text)}
              </Link>
            );
          },
        },
        {
          header: t`Closed Won Influence`,
          id: 'closedWonInfluenced',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('closedWonInfluenced'),
          infoPopover: (
            <Trans>
              Closed won influence is given to each offline event type with at least one visit
              during the date range chosen and within the timeline of a closed won opportunity.{' '}
              <SupportLink variant="body2" color="static-green" path="CLOSED_WON_INFLUENCE">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.closedWonInfluenced;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListClosedwonInfluence': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
        {
          header: t`# Closed Won Influenced`,
          id: 'countClosedwonInfluenced',
          size: 160,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('countClosedwonInfluenced'),
          infoPopover: (
            <Trans>
              Number of closed-won opportunities with influence attributed to this vendor/campaign.
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.countClosedwonInfluenced;

            return text === 0 ? (
              numberFormat(text)
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListClosedwonInfluence': field.campaignType.id,
                })}
              >
                {numberFormat(text)}
              </Link>
            );
          },
        },
        {
          header: t`Linear Pipeline`,
          id: 'pipelineLinearAttribution',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineLinearAttribution'),
          infoPopover: (
            <Trans>
              Linear pipeline accounts for all interactions within the influence window until the
              opportunity creation date and divides the pipeline value equally to each interaction.{' '}
              <SupportLink variant="body2" color="static-green" path="PIPELINE_LINEAR_ATTRIBUTION">
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.pipelineLinearAttribution;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListPipelineLinear': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
        {
          header: t`Linear Closed Won`,
          id: 'closedWonLinearAttribution',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('closedWonLinearAttribution'),
          infoPopover: (
            <Trans>
              Linear closed won accounts for all interactions within the influence window through
              the opportunity close date and divides the pipeline value equally to each interaction.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="CLOSED_WON_LINEAR_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.closedWonLinearAttribution;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListClosedwonLinear': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
        {
          header: t`First-Touch Pipeline`,
          id: 'pipelineFirstTouchAttribution',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineFirstTouchAttribution'),
          infoPopover: (
            <Trans>
              The first offline event type which was interacted with (visit or non-digital event)
              from the beginning of the influence window to the opportunity create date.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="PIPELINE_FIRST_TOUCH_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.pipelineFirstTouchAttribution;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListPipelineFirstTouch': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
        {
          header: t`First-Touch Closed Won`,
          id: 'closedWonFirstTouchAttribution',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('closedWonFirstTouchAttribution'),
          infoPopover: (
            <Trans>
              The first offline event type which was interacted with (visit or non-digital event)
              from the beginning of the influence window to the opportunity close date.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="CLOSED_WON_FIRST_TOUCH_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.closedWonFirstTouchAttribution;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListClosedwonFirstTouch': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
        {
          header: t`Last-Touch Pipeline`,
          id: 'pipelineLastTouchAttribution',
          size: 120,
          meta: { align: 'end' },
          enableSorting: sortFields.includes('pipelineLastTouchAttribution'),
          infoPopover: (
            <Trans>
              The offline event type with the previous interaction to the create date of the
              opportunity.{' '}
              <SupportLink
                variant="body2"
                color="static-green"
                path="PIPELINE_LAST_TOUCH_ATTRIBUTION"
              >
                More Details
              </SupportLink>
            </Trans>
          ),
          filterComponent: (props) => <RangeFilter {...props} />,
          cell: ({ row }) => {
            const field = row.original;
            const text = field.pipelineLastTouchAttribution;

            return text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={ROUTES.opportunityActivityWithParams({
                  'o.campaignTypeIdListPipelineLastTouch': field.campaignType.id,
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            );
          },
        },
      ] satisfies InstantSearchDataTableColumnDef<OfflineActivityDataType>[],
    [
      maxVisits,
      audience?.name,
      audience?.isTAM,
      hoveredRow,
      stickyRow,
      disabledSeries,
      sortFields,
      occurrencesMode,
      includeViewThroughVisits,
      setIncludeViewThroughVisits,
      setOccurrencesMode,
    ],
  );

  return (
    <TableState
      tableKey="offlineActivity"
      columns={offlineActivityMetrics}
      defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
    >
      <OfflineActivityTable
        data={offlineActivity}
        columns={offlineActivityMetrics}
        isLoading={isLoading}
        error={error}
        onDisabledSeriesChange={setDisabledSeries}
      />
    </TableState>
  );
};

export default OfflineActivityTableContainer;
